import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import axios from 'axios';
import { UserContext } from '../../context/UserContext';
import { BaseUrl } from '../../BaseUrl';
import NotificationService from '../../components/NotificationService/NotificationService';
import './MyAccountPage.css';

const MyAccountPage = () => {
  const { userLoggedIn, setUserLoggedIn, user, setUser } = useContext(UserContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [submitted, setSubmitted] = useState([]);
  const [published, setPublished] = useState([]);
  const [favorites, setFavorites] = useState([]);

  // Edit profile modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({
    fullName: '',
    danceAlias: '',
    city: '',
    moveMuse: '',
    gender: '',
    ethnicity: '',
    dateOfBirth: ''
  });
  const [editDances, setEditDances] = useState([]);
  const [newDance, setNewDance] = useState({ style: '', since: '', follow: '', lead: '' });
  const [saveLoading, setSaveLoading] = useState(false);
  const [editError, setEditError] = useState('');

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${BaseUrl}/api/v1/auth/get-profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const userData = response.data.data || response.data.user;
        setCurrentUser(userData);
        setUser(userData);
        setUserLoggedIn(true);
        loadUserContent(userData, token);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setLoading(false);
    }
  };

  const loadUserContent = async (userData, token) => {
    try {
      // Load drafts/reserved topics
      const topicsResponse = await axios.get(`${BaseUrl}/api/v1/topics/user/my-topics`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (topicsResponse.data.success && topicsResponse.data.data) {
        const reservedTopics = topicsResponse.data.data.reservedTopics || [];
        const draftsList = topicsResponse.data.data.drafts || [];
        const pendingSubmissions = topicsResponse.data.data.pending || [];

        const allDrafts = reservedTopics.map(topic => {
          const draft = draftsList.find(d => d.topic?._id === topic._id || d.topic === topic._id);
          return {
            ...topic,
            articleTitle: draft?.articleTitle,
            message: draft?.message,
            wordCount: draft?.message ? draft.message.split(/\s+/).filter(w => w).length : 0,
            draftId: draft?._id
          };
        });

        setDrafts(allDrafts);
        setSubmitted(pendingSubmissions);
      }

      // Load published articles
      const publishedResponse = await axios.get(`${BaseUrl}/api/v1/blogs/user/${userData._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (publishedResponse.data.blogs) {
        const approved = publishedResponse.data.blogs.filter(b => b.status === 'approved');
        setPublished(approved);
      }

      // Load favorites
      try {
        const favResponse = await axios.get(`${BaseUrl}/api/v1/blogs/favorites`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (favResponse.data.success && favResponse.data.data) {
          setFavorites(favResponse.data.data.filter(a => a != null));
        }
      } catch (e) {
        console.log('Favorites not available');
      }

    } catch (error) {
      console.error('Error loading user content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    setUserLoggedIn(false);
    setUser(null);
    NotificationService.notifySuccess('Signed out successfully');
    navigate('/');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  // Edit profile functions
  const openEditModal = () => {
    setEditData({
      fullName: currentUser?.fullName || '',
      danceAlias: currentUser?.danceAlias || '',
      city: currentUser?.city || '',
      moveMuse: currentUser?.moveMuse || '',
      gender: currentUser?.gender || '',
      ethnicity: currentUser?.ethnicity || '',
      dateOfBirth: currentUser?.dateOfBirth ? new Date(currentUser.dateOfBirth).toISOString().split('T')[0] : ''
    });
    setEditDances(currentUser?.dances || []);
    setEditError('');
    setShowEditModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData({ ...editData, [name]: value });
  };

  const handleNewDanceChange = (e) => {
    const { name, value } = e.target;
    setNewDance({ ...newDance, [name]: value });
  };

  const addDance = () => {
    if (!newDance.style) {
      setEditError('Please select a dance style');
      return;
    }

    let sinceFormatted = '';
    if (newDance.since) {
      const [year, month] = newDance.since.split('-');
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                          'July', 'August', 'September', 'October', 'November', 'December'];
      sinceFormatted = `${monthNames[parseInt(month) - 1]} ${year}`;
    }

    setEditDances([...editDances, {
      style: newDance.style,
      years: sinceFormatted,
      follow: newDance.follow,
      lead: newDance.lead
    }]);
    setNewDance({ style: '', since: '', follow: '', lead: '' });
    setEditError('');
  };

  const removeDance = (index) => {
    setEditDances(editDances.filter((_, i) => i !== index));
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setEditError('');

    if (!editData.fullName.trim()) {
      setEditError('Please enter your full name');
      return;
    }

    setSaveLoading(true);
    const token = localStorage.getItem('token');

    try {
      const response = await axios.put(`${BaseUrl}/api/v1/auth/update-profile`, {
        fullName: editData.fullName.trim(),
        danceAlias: editData.danceAlias || undefined,
        city: editData.city || undefined,
        moveMuse: editData.moveMuse || undefined,
        gender: editData.gender || undefined,
        ethnicity: editData.ethnicity || undefined,
        dateOfBirth: editData.dateOfBirth || undefined,
        dances: editDances
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const updatedUser = { ...currentUser, ...editData, dances: editDances };
        setCurrentUser(updatedUser);
        setUser(updatedUser);
        localStorage.setItem('userData', JSON.stringify(updatedUser));
        NotificationService.notifySuccess('Profile updated successfully');
        setShowEditModal(false);
      } else {
        setEditError(response.data.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setEditError(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleReleaseTopic = async (topicId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.post(`${BaseUrl}/api/v1/topics/${topicId}/release`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        NotificationService.notifySuccess('Topic released successfully');
        setDrafts(drafts.filter(d => d._id !== topicId));
      }
    } catch (error) {
      NotificationService.notifyError('Failed to release topic');
    }
  };

  const handleUnlike = async (blogId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${BaseUrl}/api/v1/blog/${blogId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      NotificationService.notifySuccess('Removed from favorites');
      setFavorites(favorites.filter(f => f._id !== blogId));
    } catch (error) {
      NotificationService.notifyError('Failed to remove from favorites');
    }
  };

  if (loading) {
    return (
      <div className="account-page">
        <main className="main-content">
          <div className="loading-spinner">
            <Spinner animation="border" variant="secondary" />
          </div>
        </main>
      </div>
    );
  }

  if (!userLoggedIn || !currentUser) {
    return (
      <div className="account-page">
        <main className="main-content">
          <div className="login-prompt">
            <i className="bi bi-person-circle"></i>
            <h2>Sign In Required</h2>
            <p>Please sign in to view your account and manage your articles.</p>
            <Link to="/login" className="btn-login">Sign In to Continue</Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="account-page">
      <main className="main-content">
        <h1 className="page-title">My Account</h1>

        {/* Profile Card */}
        <div className="profile-card">
          <div className="profile-header">
            <div className="profile-avatar">
              {currentUser.profilePicture ? (
                <img src={currentUser.profilePicture} alt="Profile" />
              ) : (
                getInitials(currentUser.fullName)
              )}
            </div>
            <div className="profile-info">
              <h2>{currentUser.fullName || 'User'}</h2>
              <p>{currentUser.email}</p>
            </div>
            <button className="btn-edit-profile" onClick={openEditModal}>
              <i className="bi bi-pencil-square"></i> Edit Profile
            </button>
          </div>

          <div className="profile-details">
            <div className="detail-item">
              <span className="detail-label">Member Since</span>
              <span className="detail-value">
                {currentUser.createdAt ? new Date(currentUser.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : '-'}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Dance Alias</span>
              <span className="detail-value">{currentUser.danceAlias || 'Not set'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">City</span>
              <span className="detail-value">{currentUser.city || 'Not set'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Dance Styles</span>
              <div className="dance-tags">
                {currentUser.dances && currentUser.dances.length > 0 ? (
                  currentUser.dances.map((d, i) => (
                    <span key={i} className="dance-tag">{d.style}</span>
                  ))
                ) : (
                  <span className="text-muted">No dance styles added</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Drafts Section */}
        <div className="section-card">
          <div className="section-header">
            <h3 className="section-title">My Drafts</h3>
            <span className="section-badge">{drafts.length}</span>
          </div>
          {drafts.length === 0 ? (
            <div className="empty-state">
              <i className="bi bi-pencil"></i>
              <p>You don't have any drafts yet.</p>
              <Link to="/catalogue" className="btn-browse">Browse Topics</Link>
            </div>
          ) : (
            <div className="article-list">
              {drafts.map(topic => (
                <div key={topic._id} className="article-card">
                  <div className="article-info">
                    <div className="article-title">{topic.articleTitle || topic.name}</div>
                    <div className="article-meta">
                      <span className="text-muted">{topic.category || 'general'}</span>
                      &nbsp;&bull;&nbsp;Reserved on {formatDate(topic.reservedAt)}
                      {topic.wordCount > 0 && ` \u2022 ${topic.wordCount} words`}
                    </div>
                  </div>
                  <span className="article-status status-draft">Draft</span>
                  <div className="article-actions">
                    <Link to={`/write?topic=${topic._id}`} className="btn-action btn-continue">Continue Writing</Link>
                    <button className="btn-action btn-release" onClick={() => handleReleaseTopic(topic._id)}>
                      Release Topic
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submitted Articles Section */}
        <div className="section-card">
          <div className="section-header">
            <h3 className="section-title">Submitted Articles</h3>
            <span className="section-badge">{submitted.length}</span>
          </div>
          {submitted.length === 0 ? (
            <div className="empty-state">
              <i className="bi bi-send"></i>
              <p>No articles pending review.</p>
            </div>
          ) : (
            <div className="article-list">
              {submitted.map(blog => (
                <div key={blog._id} className="article-card">
                  <div className="article-info">
                    <div className="article-title">{blog.articleTitle || blog.topic?.name || 'Untitled'}</div>
                    <div className="article-meta">Submitted on {formatDate(blog.createdAt)}</div>
                  </div>
                  <span className="article-status status-pending">Pending Review</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Published Articles Section */}
        <div className="section-card">
          <div className="section-header">
            <h3 className="section-title">Published Articles</h3>
            <span className="section-badge">{published.length}</span>
          </div>
          {published.length === 0 ? (
            <div className="empty-state">
              <i className="bi bi-journal-check"></i>
              <p>No published articles yet.</p>
            </div>
          ) : (
            <div className="article-list">
              {published.map(blog => (
                <div key={blog._id} className="article-card">
                  <div className="article-info">
                    <div className="article-title">{blog.articleTitle || 'Untitled'}</div>
                    <div className="article-meta">Published on {formatDate(blog.updatedAt || blog.createdAt)}</div>
                  </div>
                  <span className="article-status status-approved">Published</span>
                  <div className="article-actions">
                    <Link to={`/blog/${blog._id}`} className="btn-action btn-view">View Article</Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Favorites Section */}
        <div className="section-card">
          <div className="section-header">
            <h3 className="section-title">
              <i className="bi bi-heart-fill text-danger me-2"></i>Favorite Articles
            </h3>
            <span className="section-badge">{favorites.length}</span>
          </div>
          {favorites.length === 0 ? (
            <div className="empty-state">
              <i className="bi bi-heart"></i>
              <p>No favorite articles yet. Like articles to save them here!</p>
              <Link to="/blogs" className="btn-browse">Browse Articles</Link>
            </div>
          ) : (
            <div className="article-list">
              {favorites.map(blog => (
                <div key={blog._id} className="article-card">
                  <div className="article-info">
                    <div className="article-title">{blog.articleTitle || 'Untitled'}</div>
                    <div className="article-meta">
                      By {blog.author?.fullName || 'Move n Muse'}
                      &nbsp;&bull;&nbsp;{formatDate(blog.createdAt)}
                    </div>
                  </div>
                  <button className="btn-unlike" onClick={() => handleUnlike(blog._id)} title="Remove from favorites">
                    <i className="bi bi-heart-fill text-danger"></i>
                  </button>
                  <div className="article-actions">
                    <Link to={`/blog/${blog._id}`} className="btn-action btn-view">Read Article</Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sign Out Button */}
        <div className="text-center mt-4">
          <button onClick={handleSignOut} className="btn-action btn-release" style={{ padding: '0.75rem 2rem' }}>
            <i className="bi bi-box-arrow-right me-2"></i>Sign Out
          </button>
        </div>
      </main>

      {/* Edit Profile Modal */}
      {showEditModal && (
        <div className="edit-modal-backdrop" onClick={(e) => e.target === e.currentTarget && setShowEditModal(false)}>
          <div className="edit-modal">
            <button type="button" className="close-btn" onClick={() => setShowEditModal(false)}>
              <i className="bi bi-x-lg"></i>
            </button>
            <h3 className="edit-modal-title">Edit Profile</h3>

            <form onSubmit={saveProfile}>
              <div className="form-group">
                <label>Name</label>
                <input type="text" name="fullName" className="form-control" placeholder="Your full name"
                  value={editData.fullName} onChange={handleEditChange} />
              </div>

              <div className="form-group">
                <label>Dance Alias</label>
                <input type="text" name="danceAlias" className="form-control" placeholder="Your dance nickname"
                  value={editData.danceAlias} onChange={handleEditChange} />
              </div>

              <div className="form-group">
                <label>City</label>
                <input type="text" name="city" className="form-control" placeholder="Where you dance"
                  value={editData.city} onChange={handleEditChange} />
              </div>

              <div className="form-group">
                <label>I Move n Muse:</label>
                <input type="text" name="moveMuse" className="form-control" placeholder="What inspires your dance journey?"
                  value={editData.moveMuse} onChange={handleEditChange} />
              </div>

              <h5 style={{ fontFamily: "'Playfair Display', serif", color: 'var(--purple-accent)', margin: '1.5rem 0 1rem', textAlign: 'center' }}>
                A little about me
              </h5>

              <div className="form-group">
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <select name="gender" className="form-control" style={{ flex: 1, minWidth: '100px' }}
                    value={editData.gender} onChange={handleEditChange}>
                    <option value="">Gender</option>
                    <option value="female">Female</option>
                    <option value="male">Male</option>
                    <option value="non-binary">Non-binary</option>
                    <option value="prefer-not-to-say">Prefer not to say</option>
                  </select>
                  <select name="ethnicity" className="form-control" style={{ flex: 1, minWidth: '100px' }}
                    value={editData.ethnicity} onChange={handleEditChange}>
                    <option value="">Ethnicity</option>
                    <option value="african">African</option>
                    <option value="asian">Asian</option>
                    <option value="caribbean">Caribbean</option>
                    <option value="caucasian">Caucasian</option>
                    <option value="hispanic">Hispanic/Latino</option>
                    <option value="middle-eastern">Middle Eastern</option>
                    <option value="mixed">Mixed</option>
                    <option value="other">Other</option>
                  </select>
                  <input type="date" name="dateOfBirth" className="form-control" style={{ flex: 1, minWidth: '120px' }}
                    value={editData.dateOfBirth} onChange={handleEditChange} />
                </div>
              </div>

              <h5 style={{ fontFamily: "'Playfair Display', serif", color: 'var(--purple-accent)', margin: '1.5rem 0 1rem', textAlign: 'center' }}>
                My Dances
              </h5>

              <div className="new-dance-row">
                <div className="dance-form-row">
                  <select name="style" className="form-control" style={{ flex: 2, minWidth: '120px' }}
                    value={newDance.style} onChange={handleNewDanceChange}>
                    <option value="">Dance Style</option>
                    <option value="Salsa">Salsa</option>
                    <option value="Bachata">Bachata</option>
                    <option value="Kizomba">Kizomba</option>
                    <option value="Zouk">Zouk</option>
                    <option value="Tango">Tango</option>
                    <option value="Swing">Swing</option>
                    <option value="West Coast Swing">West Coast Swing</option>
                    <option value="Hip Hop">Hip Hop</option>
                    <option value="Contemporary">Contemporary</option>
                    <option value="Ballet">Ballet</option>
                    <option value="Other">Other</option>
                  </select>
                  <input type="month" name="since" className="form-control" style={{ flex: 1, minWidth: '130px' }}
                    value={newDance.since} onChange={handleNewDanceChange} />
                </div>
                <div className="dance-form-row">
                  <select name="follow" className="form-control" style={{ flex: 1, minWidth: '100px' }}
                    value={newDance.follow} onChange={handleNewDanceChange}>
                    <option value="">Follow</option>
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                    <option value="n/a">N/A</option>
                  </select>
                  <select name="lead" className="form-control" style={{ flex: 1, minWidth: '100px' }}
                    value={newDance.lead} onChange={handleNewDanceChange}>
                    <option value="">Lead</option>
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                    <option value="n/a">N/A</option>
                  </select>
                  <button type="button" className="btn-add-dance-style" onClick={addDance} title="Add Dance">
                    <i className="bi bi-plus-lg"></i>
                  </button>
                </div>
              </div>

              <div>
                {editDances.length === 0 ? (
                  <p className="text-muted small">No dance styles added yet</p>
                ) : (
                  editDances.map((dance, index) => (
                    <div key={index} className="dance-list-item">
                      <span>
                        {index + 1}. {dance.style}; {dance.years || 'N/A'}; Follow - {dance.follow || 'N/A'}; Lead - {dance.lead || 'N/A'}
                      </span>
                      <button type="button" className="btn-remove-dance" onClick={() => removeDance(index)}>
                        <i className="bi bi-x-circle"></i>
                      </button>
                    </div>
                  ))
                )}
              </div>

              {editError && <div className="text-danger small mt-2">{editError}</div>}

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button type="submit" className="btn-save" disabled={saveLoading}>
                  {saveLoading ? <><Spinner animation="border" size="sm" className="me-2" />Saving...</> : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyAccountPage;
